import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAdminMe } from './hooks/useAdminMe.js'

/**
 * Auth gate for admin pages. Checks the admin session via GET /api/admin/auth/me:
 * - loading → spinner-ish placeholder
 * - resolved null (401) → redirect to /admin/login
 * - resolved session → render children
 * - errored (500, network) → surface the error, do NOT bounce to login.
 *   A 500 on /me is a real problem and silently redirecting hides it.
 */
export function AdminRoute({ children }: { children: ReactNode }) {
  const { data, isLoading, isError, error, refetch } = useAdminMe()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="admin-root">
        <div className="admin-main">
          <p className="admin-audit-meta">Checking session…</p>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="admin-root">
        <div className="admin-main admin-error-state">
          <p>Couldn't verify your session.</p>
          <p className="admin-audit-meta">
            {error instanceof Error ? error.message : 'Unexpected error'}
          </p>
          <button
            type="button"
            className="admin-btn"
            onClick={() => void refetch()}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!data) {
    return <Navigate to="/admin/login" replace />
  }

  // Admin created with a temporary password must change it before using any
  // other admin screen. Pin them to /admin/account (which hosts the change-
  // password form) until done; the backend enforces the same rule on the API.
  if (data.admin.must_change_password && location.pathname !== '/admin/account') {
    return <Navigate to="/admin/account" replace />
  }

  return <>{children}</>
}
